import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { checkCampaignExists, fetchCampaignListsAndCompany, fetchCategories } from "src/services/EngagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";
import type { CampaignChannel } from "../../Engagements/EngagementInterfaces";
import { buildSetupDraftData } from "../../Engagements/Campaigns/shared/CampaignDraftUtils";
import type { CampaignDraftState } from "../../Engagements/Campaigns/shared/UseCampaignDraft";
import { COMPANY_NAME_STORAGE_KEY } from "../../Engagements/Campaigns/CampaignSetup/CampaignSetupConstants";

const UNSUBSCRIBED_LIST_KEYWORD = "unsubscribed";

const getStoredCredentials = () => {
  const tenantId = localStorage.getItem("tenant_id");
  const apiKey = localStorage.getItem("api_key");
  return tenantId && apiKey ? { tenantId, apiKey } : null;
};

const getUnsubscribedLists = (options: AudienceListOption[]): string[] =>
  options
    .map(option => option.name)
    .filter(name => name.toLowerCase().includes(UNSUBSCRIBED_LIST_KEYWORD));

export interface ChatCampaignSetupResult {
  channel: CampaignChannel;
  campaignName: string;
  companyName: string;
  sendTo: string[];
  excludeList: string[];
  isAIGeneratorOn: boolean;
}

interface UseChatCampaignSetupParams {
  onSubmit: (_result: ChatCampaignSetupResult) => void;
  presetChannel?: CampaignChannel;
  draftController: CampaignDraftState;
}

export const useChatCampaignSetup = ({
  onSubmit,
  presetChannel,
  draftController,
}: UseChatCampaignSetupParams) => {
  const { t, i18n } = useTranslation();

  const [selectedChannel, setChannel] = useState<CampaignChannel | null>("email");
  const channel = presetChannel ?? selectedChannel;
  const [campaignName, setCampaignName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [sendTo, setSendTo] = useState<string[]>([]);
  const [excludeList, setExcludeList] = useState<string[]>([]);
  const [audienceOptions, setAudienceOptions] = useState<AudienceListOption[]>([]);
  const [isListsLoading, setIsListsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAIGeneratorOn, setIsAIGeneratorOn] = useState(true);
  const [isAIGeneratorDisabled, setIsAIGeneratorDisabled] = useState(false);

  useEffect(() => {
    let isActive = true;
    const credentials = getStoredCredentials();

    const loadListsAndCompany = async () => {
      if (!credentials) {
        setIsListsLoading(false);
        return;
      }
      try {
        const data = await fetchCampaignListsAndCompany(
          credentials.tenantId,
          credentials.apiKey,
          getApiLangFromI18n(i18n)
        );
        if (!isActive) {
          return;
        }
        const options = registerAudienceListLabels(toAudienceListOptions(data));
        setAudienceOptions(options);
        setExcludeList(current => (current.length ? current : getUnsubscribedLists(options)));

        if (data.company_name_engagements) {
          setCompanyName(data.company_name_engagements);
          localStorage.setItem(COMPANY_NAME_STORAGE_KEY, data.company_name_engagements);
        }
      } catch {
        if (isActive) {
          message.error(i18n.t("campaignFlow.createCampaignNew.listsLoadFailed"));
        }
      } finally {
        if (isActive) {
          setIsListsLoading(false);
        }
      }
    };

    const checkSitemapAvailability = async () => {
      try {
        const response = await fetchCategories(getApiLangFromI18n(i18n));
        const websiteCategories =
          response?.website_categories || response?.website_urls || response?.categories;
        if (!websiteCategories?.length) {
          throw new Error("No categories found");
        }
      } catch {
        if (!isActive) {
          return;
        }
        setIsAIGeneratorDisabled(true);
        setIsAIGeneratorOn(false);
        message.info(i18n.t("campaignFlow.createCampaignNew.aiDisableMessage"));
      }
    };

    loadListsAndCompany();
    checkSitemapAvailability();

    return () => {
      isActive = false;
    };
  }, [i18n]);

  const excludeOptions = useMemo(
    () => audienceOptions.filter(option => !sendTo.includes(option.name)),
    [audienceOptions, sendTo]
  );

  const handleAIGeneratorChange = useCallback(
    (checked: boolean) => {
      if (!isAIGeneratorDisabled) {
        setIsAIGeneratorOn(checked);
      }
    },
    [isAIGeneratorDisabled]
  );

  const canSubmit =
    !isSubmitting &&
    Boolean(channel) &&
    Boolean(campaignName.trim()) &&
    Boolean(companyName.trim()) &&
    sendTo.length > 0;

  const handleSubmit = useCallback(async () => {
    const trimmedCampaignName = campaignName.trim();
    const trimmedCompanyName = companyName.trim();

    if (!channel) {
      message.error(t("campaignFlow.createCampaignNew.selectChannel"));
      return;
    }
    if (!trimmedCampaignName) {
      message.error(t("campaignFlow.createCampaignNew.enterCampaignName"));
      return;
    }
    if (!trimmedCompanyName) {
      message.error(t("campaignFlow.createCampaignNew.companyNameRequired"));
      return;
    }
    if (!sendTo.length) {
      message.error(t("campaignFlow.createCampaignNew.selectSendTo"));
      return;
    }

    setIsSubmitting(true);
    try {
      await checkCampaignExists({
        tenant_id: Number(localStorage.getItem("tenant_id") || 0),
        campaign_name: trimmedCampaignName,
        ...(draftController.draftId !== null && { draft_id: draftController.draftId }),
      });

      onSubmit({
        channel,
        campaignName: trimmedCampaignName,
        companyName: trimmedCompanyName,
        sendTo,
        excludeList,
        isAIGeneratorOn,
      });
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      message.error(detail || t("campaignFlow.createCampaignNew.campaignExists"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    campaignName,
    channel,
    companyName,
    draftController.draftId,
    excludeList,
    isAIGeneratorOn,
    onSubmit,
    sendTo,
    t,
  ]);

  const { save: saveToDraft } = draftController;
  const saveDraft = useCallback(async () => {
    if (channel === "whatsapp") {
      message.info(t("campaignFlow.draft.whatsappUnavailable"));
      return false;
    }
    return saveToDraft(buildSetupDraftData({ isAIGeneratorOn, campaignName, sendTo, excludeList }));
  }, [campaignName, channel, excludeList, isAIGeneratorOn, saveToDraft, sendTo, t]);

  const reset = useCallback(() => {
    setCampaignName("");
    setSendTo([]);
    setExcludeList(getUnsubscribedLists(audienceOptions));
  }, [audienceOptions]);

  return {
    channel,
    campaignName,
    companyName,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    isListsLoading,
    canSubmit,
    isAIGeneratorOn,
    isAIGeneratorDisabled,
    setChannel,
    setCampaignName,
    setSendTo,
    setExcludeList,
    handleAIGeneratorChange,
    handleSubmit,
    saveDraft,
    isSavingDraft: draftController.isSaving,
    reset,
  };
};
